import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Card,
    Select,
    DatePicker,
    Statistic,
    Tag,
    Tabs,
    Table,
    Tooltip,
    Typography,
    Spin,
    Empty,
    Button,
    Collapse,
    Space,
    Alert,
    Modal,
} from 'antd';
import {
    ArrowUpOutlined,
    ArrowDownOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    WarningOutlined,
    ExperimentOutlined,
    DiffOutlined,
} from '@ant-design/icons';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RTooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import type { ColumnsType } from 'antd/es/table';
import type {
    DashboardRunSummary,
    DashboardFilters,
    EvalPerQuestionResult,
    PromptImpactRow,
    SkillStudioDashboardSectionProps,
} from './SkillStudio.types';
import { SKILL_STUDIO_TARGET_AGENT_OPTIONS } from './SkillStudio.types';
import { useSkillStudioLayout } from './SkillStudio';
import { compareAgents } from './eval/evalAgentOrder';
import dayjs from 'dayjs';

const STANDALONE_SECTION_HEIGHT = 'calc(100vh - 160px)';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const AGENT_COLORS = [
    '#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1',
    '#13c2c2', '#eb2f96', '#fa8c16', '#2f54eb', '#a0d911',
];

function titleCase(s: string): string {
    return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDurationSec(ms: number | null): string {
    if (ms == null) return '-';
    const sec = Math.round(ms / 1000);
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function scoreColor(pct: number): string {
    if (pct >= 70) return '#52c41a';
    if (pct >= 40) return '#faad14';
    return '#ff4d4f';
}

// ────────────────────────────────────────────────────────────────
// Delta tag component
// ────────────────────────────────────────────────────────────────
const DeltaTag: React.FC<{ current: number | null; previous: number | null; suffix?: string; invert?: boolean }> = ({
    current, previous, suffix = '', invert = false,
}) => {
    if (current == null || previous == null) return null;
    const diff = current - previous;
    if (Math.abs(diff) < 0.001) return null;
    const positive = invert ? diff < 0 : diff > 0;
    const formatted = `${diff > 0 ? '+' : ''}${(diff * (suffix === '%' ? 100 : 1)).toFixed(1)}${suffix}`;
    return (
        <Tag
            color={positive ? 'green' : 'red'}
            icon={positive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            style={{ marginLeft: 8, fontSize: 11 }}
        >
            {formatted}
        </Tag>
    );
};

// ════════════════════════════════════════════════════════════════
// Main component
// ════════════════════════════════════════════════════════════════
const SkillStudioDashboardSection: React.FC<SkillStudioDashboardSectionProps> = ({
    runs,
    metricPoints,
    filters,
    onFiltersChange,
    loading,
    batchLoading,
    detailCache,
    onLoadRunDetail,
    onLoadBatchDetails,
    onRefresh,
    promptChanges,
    promptImpact,
    insights,
    questionSetOptions,
    configOptions,
    tenantOptions,
    targetAgentOptions = SKILL_STUDIO_TARGET_AGENT_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
    })),
}) => {
    const skillStudioLayout = useSkillStudioLayout();
    const sectionHeight = skillStudioLayout != null ? '100%' : STANDALONE_SECTION_HEIGHT;

    const [activeTab, setActiveTab] = useState('trends');
    const [diffModal, setDiffModal] = useState<{ sha: string; message: string } | null>(null);
    const runsKey = useMemo(() => runs.map((r) => r.runId).join(','), [runs]);
    const batchDetailsFetchedForRunsKeyRef = useRef<string | null>(null);

    // Auto-load batch details once per run set for tabs that need per-question data (avoid refetch on every tab change)
    useEffect(() => {
        const needsBatch =
            activeTab === 'trends' || activeTab === 'questions' || activeTab === 'prompt-impact';
        if (!needsBatch) return;
        if (runs.length === 0) {
            batchDetailsFetchedForRunsKeyRef.current = null;
            return;
        }
        if (batchDetailsFetchedForRunsKeyRef.current === runsKey) return;
        batchDetailsFetchedForRunsKeyRef.current = runsKey;
        onLoadBatchDetails();
    }, [activeTab, runsKey, runs.length, onLoadBatchDetails]);

    // Load latest run detail for KPI cards on mount
    useEffect(() => {
        if (runs.length > 0) {
            const latest = runs[runs.length - 1];
            if (!detailCache.has(latest.runId)) {
                onLoadRunDetail(latest.runId).catch(() => {});
            }
            if (runs.length > 1) {
                const prev = runs[runs.length - 2];
                if (!detailCache.has(prev.runId)) {
                    onLoadRunDetail(prev.runId).catch(() => {});
                }
            }
        }
    }, [runs, detailCache, onLoadRunDetail]);

    const handleFilterChange = (patch: Partial<DashboardFilters>) => {
        onFiltersChange(patch);
    };

    // ── KPI data ──
    const latest = runs.length > 0 ? runs[runs.length - 1] : null;
    const previous = runs.length > 1 ? runs[runs.length - 2] : null;

    const avgSimilarity = (run: DashboardRunSummary | null): number | null => {
        if (!run) return null;
        const simKeys = Object.entries(run.metrics).filter(([k]) => k.includes('.similarity'));
        if (simKeys.length === 0) return null;
        return simKeys.reduce((sum, [, v]) => sum + v, 0) / simKeys.length;
    };

    const latestSim = avgSimilarity(latest);
    const prevSim = avgSimilarity(previous);

    // ────────────────────────────────────────────────────────────
    // Section A: Filters
    // ────────────────────────────────────────────────────────────
    const filtersRow = (
        <Card size="small" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <Select
                    allowClear
                    placeholder="Branch"
                    style={{ width: 200 }}
                    value={filters.tenant}
                    onChange={(v) => handleFilterChange({ tenant: v })}
                    showSearch
                    options={tenantOptions}
                />
                <Select
                    allowClear
                    placeholder="Question Set"
                    style={{ width: 200 }}
                    value={filters.questionSet}
                    onChange={(v) => handleFilterChange({ questionSet: v })}
                    options={questionSetOptions}
                />
                <Select
                    allowClear
                    placeholder="Target Agent"
                    style={{ width: 160 }}
                    value={filters.targetAgent}
                    onChange={(v) => handleFilterChange({ targetAgent: v })}
                    options={targetAgentOptions.filter((o) => o.value)}
                />
                <Select
                    allowClear
                    placeholder="Config"
                    style={{ width: 200 }}
                    value={filters.configName}
                    onChange={(v) => handleFilterChange({ configName: v })}
                    options={configOptions}
                />
                <RangePicker
                    style={{ width: 280 }}
                    value={filters.dateRange ? [dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])] : null}
                    onChange={(dates) => {
                        if (dates && dates[0] && dates[1]) {
                            handleFilterChange({ dateRange: [dates[0].toISOString(), dates[1].toISOString()] });
                        } else {
                            handleFilterChange({ dateRange: undefined });
                        }
                    }}
                />
                <div style={{ flex: 1 }} />
                <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading} size="small">
                    Refresh
                </Button>
            </div>
        </Card>
    );

    // ────────────────────────────────────────────────────────────
    // Section B: KPI Cards
    // ────────────────────────────────────────────────────────────
    const kpiCards = (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <Card size="small" style={{ flex: '1 1 200px', minWidth: 180 }}>
                <Statistic
                    title="Pass Rate"
                    value={latest?.passRate != null ? Math.round(latest.passRate * 100) : '-'}
                    suffix={latest?.passRate != null ? '%' : ''}
                    valueStyle={{ color: latest?.passRate != null ? scoreColor(latest.passRate * 100) : undefined }}
                />
                <DeltaTag current={latest?.passRate ?? null} previous={previous?.passRate ?? null} suffix="%" />
            </Card>
            <Card size="small" style={{ flex: '1 1 200px', minWidth: 180 }}>
                <Statistic
                    title="Avg Similarity"
                    value={latestSim != null ? latestSim.toFixed(2) : '-'}
                    valueStyle={{ color: latestSim != null ? scoreColor(latestSim >= 0 && latestSim <= 1 ? latestSim * 100 : (latestSim / 5) * 100) : undefined }}
                />
                <DeltaTag current={latestSim} previous={prevSim} />
            </Card>
            <Card size="small" style={{ flex: '1 1 200px', minWidth: 180 }}>
                <Statistic
                    title="Error Rate"
                    value={latest?.errorRate != null ? Math.round(latest.errorRate * 100) : '-'}
                    suffix={latest?.errorRate != null ? '%' : ''}
                    valueStyle={{ color: latest?.errorRate != null ? (latest.errorRate === 0 ? '#52c41a' : '#ff4d4f') : undefined }}
                />
                <DeltaTag current={latest?.errorRate ?? null} previous={previous?.errorRate ?? null} suffix="%" invert />
            </Card>
            <Card size="small" style={{ flex: '1 1 200px', minWidth: 180 }}>
                <Statistic
                    title="Duration"
                    value={formatDurationSec(latest?.durationMs ?? null)}
                />
                {latest?.durationMs != null && previous?.durationMs != null && (
                    <DeltaTag
                        current={latest.durationMs / 1000}
                        previous={previous.durationMs / 1000}
                        suffix="s"
                        invert
                    />
                )}
            </Card>
        </div>
    );

    // ────────────────────────────────────────────────────────────
    // Section C: Trends tab
    // ────────────────────────────────────────────────────────────
    // Merge prompt change markers into chart data as extra entries sorted by time.
    // Each marker has _promptChange metadata; metric values are undefined so lines gap.
    type ChartRow = Record<string, unknown>;

    const promptMarkers = useMemo(() => {
        return promptChanges.map((pc) => ({
            _ts: new Date(pc.date).getTime(),
            _promptChange: { sha: pc.sha.slice(0, 7), message: pc.message, author: pc.author },
            _marker: 0,
            date: `\u270E ${formatDate(pc.date)}`,
        }));
    }, [promptChanges]);

    const mergeWithMarkers = useCallback((data: ChartRow[], tsKey: string): ChartRow[] => {
        if (promptMarkers.length === 0) return data;
        const withTs = data.map((d) => ({ ...d, _ts: new Date(d[tsKey] as string).getTime() }));
        const merged = [...withTs, ...promptMarkers].sort((a, b) => (a._ts as number) - (b._ts as number));
        return merged;
    }, [promptMarkers]);

    const similarityDataRaw = useMemo(() => {
        const simPoints = metricPoints.filter((p) => p.metricKey.includes('.similarity'));
        const byRun = new Map<number, ChartRow>();
        for (const p of simPoints) {
            const agent = p.metricKey.split('.')[0] || p.metricKey.split('_')[0];
            if (!byRun.has(p.runId)) byRun.set(p.runId, { date: formatDate(p.startedAt), startedAt: p.startedAt, runId: p.runId });
            byRun.get(p.runId)![agent] = p.value;
        }
        return Array.from(byRun.values());
    }, [metricPoints]);

    const similarityData = useMemo(() => mergeWithMarkers(similarityDataRaw, 'startedAt'), [similarityDataRaw, mergeWithMarkers]);

    const similarityAgents = useMemo(() => {
        const agents = new Set<string>();
        for (const p of metricPoints) {
            if (p.metricKey.includes('.similarity')) {
                agents.add(p.metricKey.split('.')[0] || p.metricKey.split('_')[0]);
            }
        }
        return Array.from(agents).sort();
    }, [metricPoints]);

    const passRateDataRaw = useMemo(() => {
        return runs
            .filter((r) => r.passRate != null)
            .map((r) => ({
                date: formatDate(r.startedAt),
                startedAt: r.startedAt,
                runId: r.runId,
                passRate: Math.round((r.passRate ?? 0) * 100),
                errorRate: Math.round((r.errorRate ?? 0) * 100),
            }));
    }, [runs]);

    const passRateData = useMemo(() => mergeWithMarkers(passRateDataRaw, 'startedAt'), [passRateDataRaw, mergeWithMarkers]);

    const durationDataRaw = useMemo(() => {
        return runs
            .filter((r) => r.durationMs != null)
            .map((r) => ({
                date: formatDate(r.startedAt),
                startedAt: r.startedAt,
                runId: r.runId,
                duration: Math.round((r.durationMs ?? 0) / 1000),
            }));
    }, [runs]);

    const durationData = useMemo(() => mergeWithMarkers(durationDataRaw, 'startedAt'), [durationDataRaw, mergeWithMarkers]);

    const CustomTooltip: React.FC<{ active?: boolean; payload?: Array<{ dataKey: string; value: unknown; name: string; color: string }>; label?: string }> = ({ active, payload, label }) => {
        if (!active || !payload || payload.length === 0) return null;
        const entry = payload[0] as unknown as { payload: ChartRow };
        const pc = entry?.payload?._promptChange as { sha: string; message: string; author: string } | undefined;
        if (pc) {
            return (
                <div style={{ background: '#fff', border: '1px solid #722ed1', borderRadius: 6, padding: '8px 12px', maxWidth: 280 }}>
                    <div style={{ fontWeight: 600, color: '#722ed1', fontSize: 12, marginBottom: 4 }}>Prompt Change</div>
                    <div style={{ fontSize: 11, color: '#333' }}>{pc.message}</div>
                    <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>{pc.sha} &middot; {pc.author}</div>
                </div>
            );
        }
        const metricEntries = payload.filter((p) => p.value != null && p.dataKey !== '_marker');
        if (metricEntries.length === 0) return null;
        return (
            <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 6, padding: '8px 12px' }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{label}</div>
                {metricEntries.map((p, i) => (
                    <div key={i} style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
                        <span>{p.name}: <strong>{typeof p.value === 'number' ? p.value : String(p.value)}</strong></span>
                    </div>
                ))}
            </div>
        );
    };

    const markerDot = (props: Record<string, unknown>) => {
        const { cx, cy } = props as { cx: number; cy: number };
        return <polygon key={`pd-${cx}`} points={`${cx},${cy - 8} ${cx - 6},${cy + 3} ${cx + 6},${cy + 3}`} fill="#722ed1" stroke="#fff" strokeWidth={1} />;
    };

    const trendsContent = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {promptMarkers.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    <span style={{ color: '#722ed1', fontSize: 14 }}>&#9650;</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Purple markers on the charts indicate prompt changes — hover for details
                    </Text>
                </div>
            )}

            {/* Chart 1: similarity over time */}
            <Card size="small" title="Per-Agent Similarity Over Time">
                {similarityDataRaw.length === 0 ? (
                    <Empty description="No similarity data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={similarityData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                            <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} />
                            <RTooltip content={<CustomTooltip />} />
                            <Legend />
                            {similarityAgents.map((agent, i) => (
                                <Line
                                    key={agent}
                                    type="monotone"
                                    dataKey={agent}
                                    name={titleCase(agent)}
                                    stroke={AGENT_COLORS[i % AGENT_COLORS.length]}
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    connectNulls
                                />
                            ))}
                            {promptMarkers.length > 0 && (
                                <Line type="monotone" dataKey="_marker" name="Prompt Change" stroke="none" legendType="none" dot={markerDot} connectNulls={false} />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Card>

            {/* Chart 2: pass rate + error rate */}
            <Card size="small" title="Pass Rate & Error Rate Over Time" extra={batchLoading ? <Spin size="small" /> : null}>
                {passRateDataRaw.length === 0 ? (
                    <Empty description={batchLoading ? 'Loading run details...' : 'No data'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={passRateData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                            <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                            <RTooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line type="monotone" dataKey="passRate" name="Pass Rate" stroke="#52c41a" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                            <Line type="monotone" dataKey="errorRate" name="Error Rate" stroke="#ff4d4f" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                            {promptMarkers.length > 0 && (
                                <Line type="monotone" dataKey="_marker" name="Prompt Change" stroke="none" legendType="none" dot={markerDot} connectNulls={false} />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Card>

            {/* Chart 3: duration */}
            <Card size="small" title="Run Duration Over Time">
                {durationDataRaw.length === 0 ? (
                    <Empty description="No data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={durationData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                            <YAxis unit="s" tick={{ fontSize: 11 }} />
                            <RTooltip content={<CustomTooltip />} />
                            <Line type="monotone" dataKey="duration" name="Duration" stroke="#1890ff" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                            {promptMarkers.length > 0 && (
                                <Line type="monotone" dataKey="_marker" name="Prompt Change" stroke="none" legendType="none" dot={markerDot} connectNulls={false} />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Card>
        </div>
    );

    // ────────────────────────────────────────────────────────────
    // Section D: Consistency tab
    // ────────────────────────────────────────────────────────────
    const consistencyRuns = useMemo(() => runs.filter((r) => r.consistency != null), [runs]);

    const consistencyChartData = useMemo(() => {
        const allFields = new Set<string>();
        for (const r of consistencyRuns) {
            if (r.consistency) {
                for (const f of Object.keys(r.consistency.aggregate)) allFields.add(f);
            }
        }
        return consistencyRuns.map((r) => {
            const row: Record<string, unknown> = { date: formatDate(r.startedAt), runId: r.runId };
            if (r.consistency) {
                for (const f of allFields) {
                    row[f] = r.consistency.aggregate[f]?.mean_exact_match ?? null;
                }
            }
            return row;
        });
    }, [consistencyRuns]);

    const consistencyFields = useMemo(() => {
        const fields = new Set<string>();
        for (const r of consistencyRuns) {
            if (r.consistency) {
                for (const f of Object.keys(r.consistency.aggregate)) fields.add(f);
            }
        }
        return Array.from(fields).sort(compareAgents);
    }, [consistencyRuns]);

    const consistencyColumns: ColumnsType<DashboardRunSummary> = useMemo(() => {
        const base: ColumnsType<DashboardRunSummary> = [
            { title: 'Run', dataIndex: 'runId', key: 'runId', width: 60 },
            { title: 'Date', dataIndex: 'startedAt', key: 'date', width: 160, render: (v: string) => formatDate(v) },
            { title: 'Repeats', dataIndex: 'repeats', key: 'repeats', width: 70, align: 'center', render: (v: number | null) => v != null ? <Tag color="blue">{v}x</Tag> : '-' },
        ];
        const fieldCols: ColumnsType<DashboardRunSummary> = consistencyFields.map((f) => ({
            title: titleCase(f),
            key: f,
            width: 120,
            align: 'center' as const,
            render: (_: unknown, record: DashboardRunSummary) => {
                const val = record.consistency?.aggregate[f]?.mean_exact_match;
                if (val == null) return '-';
                const pct = Math.round(val * 100);
                return <Tag color={pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'error'}>{pct}%</Tag>;
            },
        }));
        return [...base, ...fieldCols];
    }, [consistencyFields]);

    const consistencyContent = consistencyRuns.length === 0 ? (
        <Empty description="No consistency runs found" />
    ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <Card size="small" title="Mean Exact Match by Field Over Time">
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={consistencyChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 1]} tickFormatter={(v) => `${Math.round(Number(v) * 100)}%`} tick={{ fontSize: 11 }} />
                        <RTooltip formatter={(v, name) => [`${Math.round(Number(v) * 100)}%`, name]} />
                        <Legend />
                        {consistencyFields.map((f, i) => (
                            <Line
                                key={f}
                                type="monotone"
                                dataKey={f}
                                name={titleCase(f)}
                                stroke={AGENT_COLORS[i % AGENT_COLORS.length]}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                connectNulls
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </Card>

            <Card size="small" title="Consistency Runs">
                <Table
                    size="small"
                    rowKey="runId"
                    dataSource={consistencyRuns}
                    columns={consistencyColumns}
                    pagination={false}
                    expandable={{
                        expandedRowRender: (record) => {
                            if (!record.consistency?.per_question?.length) return <Text type="secondary">No per-question data</Text>;
                            return (
                                <Collapse
                                    size="small"
                                    items={record.consistency.per_question.map((pq, idx) => ({
                                        key: String(idx),
                                        label: (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <Text ellipsis style={{ flex: 1 }}>{pq.query}</Text>
                                                <Tag>{pq.runs} runs</Tag>
                                                {Object.entries(pq.fields)
                                                    .sort(([a], [b]) => compareAgents(a, b))
                                                    .map(([f, d]) => (
                                                    <Tooltip key={f} title={`${titleCase(f)}: ${Math.round(d.exact_match_rate * 100)}%`}>
                                                        {d.exact_match_rate === 1.0
                                                            ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                            : d.exact_match_rate === 0.0
                                                                ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                                                : <WarningOutlined style={{ color: '#faad14' }} />}
                                                    </Tooltip>
                                                ))}
                                            </div>
                                        ),
                                        children: (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {Object.entries(pq.fields)
                                                    .sort(([a], [b]) => compareAgents(a, b))
                                                    .map(([fieldName, detail]) => (
                                                    <div key={fieldName}>
                                                        <Space size={8} style={{ marginBottom: 4 }}>
                                                            <Text strong style={{ fontSize: 12 }}>{titleCase(fieldName)}</Text>
                                                            <Tag color={detail.exact_match_rate === 1 ? 'success' : detail.exact_match_rate === 0 ? 'error' : 'warning'}>
                                                                {Math.round(detail.exact_match_rate * 100)}% match
                                                            </Tag>
                                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                                {detail.unique_outputs} unique output{detail.unique_outputs !== 1 ? 's' : ''}
                                                            </Text>
                                                        </Space>
                                                        {Array.isArray(detail.outputs_per_run) && detail.outputs_per_run.length > 0 && (
                                                            <Table
                                                                size="small"
                                                                pagination={false}
                                                                rowKey="run_index"
                                                                dataSource={[...detail.outputs_per_run].sort((a, b) => a.run_index - b.run_index)}
                                                                columns={[
                                                                    { title: 'Run', dataIndex: 'run_index', key: 'run_index', width: 60, align: 'center' },
                                                                    {
                                                                        title: 'Output', dataIndex: 'output', key: 'output',
                                                                        render: (v: unknown) => {
                                                                            const text = v == null ? '-' : typeof v === 'string' ? v : JSON.stringify(v, null, 2);
                                                                            return <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>{text}</div>;
                                                                        },
                                                                    },
                                                                ]}
                                                            />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ),
                                    }))}
                                />
                            );
                        },
                    }}
                />
            </Card>
        </div>
    );

    // ────────────────────────────────────────────────────────────
    // Section E: Question Tracker tab
    // ────────────────────────────────────────────────────────────
    const allQuestionResults = useMemo(() => {
        const results: Array<{ runId: number; startedAt: string; result: EvalPerQuestionResult }> = [];
        for (const run of runs) {
            const detail = detailCache.get(run.runId);
            if (!detail?.per_question_results) continue;
            for (const q of detail.per_question_results) {
                results.push({ runId: run.runId, startedAt: run.startedAt, result: q });
            }
        }
        return results;
    }, [runs, detailCache]);

    const uniqueQuestions = useMemo(() => {
        const qMap = new Map<string, string>();
        for (const { result } of allQuestionResults) {
            if (!qMap.has(result.query)) qMap.set(result.query, result.query);
        }
        return Array.from(qMap.values()).sort();
    }, [allQuestionResults]);

    const [selectedQuestion, setSelectedQuestion] = useState<string | undefined>();

    const questionPassData = useMemo(() => {
        if (!selectedQuestion) return [];
        return allQuestionResults
            .filter((r) => r.result.query === selectedQuestion)
            .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime())
            .map((r) => ({
                date: formatDate(r.startedAt),
                runId: r.runId,
                pass: r.result.row_pass === true ? 1 : 0,
                error: r.result.error != null ? 1 : 0,
                color: r.result.error ? '#faad14' : r.result.row_pass ? '#52c41a' : '#ff4d4f',
            }));
    }, [selectedQuestion, allQuestionResults]);

    // Gate heatmap for selected question
    const gateHeatmapData = useMemo(() => {
        if (!selectedQuestion) return { rows: [] as Array<Record<string, unknown>>, agents: [] as string[] };
        const agents = new Set<string>();
        const rows: Array<Record<string, unknown>> = [];
        const filtered = allQuestionResults
            .filter((r) => r.result.query === selectedQuestion)
            .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());

        for (const { runId, startedAt, result } of filtered) {
            const row: Record<string, unknown> = { runId, date: formatDate(startedAt) };
            if (result.gate_metrics) {
                for (const [k, v] of Object.entries(result.gate_metrics)) {
                    const agent = k.split('.')[0];
                    agents.add(agent);
                    if (row[agent] === undefined) row[agent] = v;
                    else row[agent] = (row[agent] as boolean) && v;
                }
            }
            rows.push(row);
        }
        return { rows, agents: Array.from(agents).sort() };
    }, [selectedQuestion, allQuestionResults]);

    // Worst questions
    const worstQuestions = useMemo(() => {
        const qMap = new Map<string, { failCount: number; totalCount: number; lastPass: boolean | null }>();
        for (const { result } of allQuestionResults) {
            const existing = qMap.get(result.query) ?? { failCount: 0, totalCount: 0, lastPass: null };
            existing.totalCount++;
            if (result.row_pass === false) existing.failCount++;
            existing.lastPass = result.row_pass;
            qMap.set(result.query, existing);
        }
        return Array.from(qMap.entries())
            .map(([query, stats]) => ({ query, ...stats }))
            .filter((q) => q.failCount > 0)
            .sort((a, b) => b.failCount - a.failCount)
            .slice(0, 20);
    }, [allQuestionResults]);

    const gateHeatmapColumns: ColumnsType<Record<string, unknown>> = useMemo(() => {
        const base: ColumnsType<Record<string, unknown>> = [
            { title: 'Run', dataIndex: 'runId', key: 'runId', width: 60 },
            { title: 'Date', dataIndex: 'date', key: 'date', width: 160 },
        ];
        const agentCols: ColumnsType<Record<string, unknown>> = gateHeatmapData.agents.map((a) => ({
            title: titleCase(a),
            key: a,
            width: 80,
            align: 'center' as const,
            render: (_: unknown, record: Record<string, unknown>) => {
                const v = record[a];
                if (v === true) return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
                if (v === false) return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
                return '-';
            },
        }));
        return [...base, ...agentCols];
    }, [gateHeatmapData.agents]);

    const questionTrackerContent = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {allQuestionResults.length === 0 ? (
                <Empty description={batchLoading ? 'Loading run details...' : 'No per-question data loaded yet'}>
                    {!batchLoading && <Button onClick={onLoadBatchDetails}>Load Run Details</Button>}
                </Empty>
            ) : (
                <>
                    <Card size="small" title="Question History">
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select a question to track..."
                            style={{ width: '100%', marginBottom: 16 }}
                            value={selectedQuestion}
                            onChange={setSelectedQuestion}
                            optionFilterProp="label"
                            options={uniqueQuestions.map((q) => ({ value: q, label: q }))}
                        />
                        {selectedQuestion && questionPassData.length > 0 ? (
                            <>
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={questionPassData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                        <YAxis domain={[-0.2, 1.2]} ticks={[0, 1]} tickFormatter={(v) => Number(v) === 1 ? 'Pass' : 'Fail'} tick={{ fontSize: 11 }} />
                                        <RTooltip formatter={(v) => [Number(v) === 1 ? 'Pass' : 'Fail', 'Result']} />
                                        <Line
                                            type="stepAfter"
                                            dataKey="pass"
                                            name="Result"
                                            stroke="#1890ff"
                                            strokeWidth={2}
                                            dot={(props: Record<string, unknown>) => {
                                                const { cx, cy, index } = props as { cx: number; cy: number; index: number };
                                                const entry = questionPassData[index];
                                                return <circle key={index} cx={cx} cy={cy} r={5} fill={entry?.color ?? '#1890ff'} stroke="none" />;
                                            }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>

                                {gateHeatmapData.rows.length > 0 && gateHeatmapData.agents.length > 0 && (
                                    <>
                                        <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>Gate Results by Agent</Title>
                                        <Table
                                            size="small"
                                            rowKey="runId"
                                            dataSource={gateHeatmapData.rows}
                                            columns={gateHeatmapColumns}
                                            pagination={false}
                                        />
                                    </>
                                )}
                            </>
                        ) : selectedQuestion ? (
                            <Text type="secondary">No results found for this question</Text>
                        ) : null}
                    </Card>

                    <Card size="small" title="Worst-Performing Questions">
                        {worstQuestions.length === 0 ? (
                            <Text type="secondary">All questions passing</Text>
                        ) : (
                            <Table<{ query: string; failCount: number; totalCount: number; lastPass: boolean | null }>
                                size="small"
                                rowKey="query"
                                dataSource={worstQuestions}
                                pagination={false}
                                columns={[
                                    { title: 'Question', dataIndex: 'query', key: 'query', ellipsis: true },
                                    {
                                        title: 'Fail Count', dataIndex: 'failCount', key: 'failCount', width: 100, align: 'center',
                                        sorter: (a, b) => a.failCount - b.failCount,
                                        defaultSortOrder: 'descend',
                                        render: (v: number) => <Tag color="error">{v}</Tag>,
                                    },
                                    { title: 'Total Runs', dataIndex: 'totalCount', key: 'totalCount', width: 90, align: 'center' },
                                    {
                                        title: 'Fail Rate', key: 'rate', width: 100, align: 'center',
                                        render: (_: unknown, record) => {
                                            const pct = Math.round((record.failCount / record.totalCount) * 100);
                                            return <Tag color={pct >= 50 ? 'error' : 'warning'}>{pct}%</Tag>;
                                        },
                                    },
                                    {
                                        title: 'Last Result', dataIndex: 'lastPass', key: 'lastPass', width: 100, align: 'center',
                                        render: (v: boolean | null) => v == null ? '-' : v
                                            ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                            : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
                                    },
                                ]}
                                onRow={(record) => ({
                                    onClick: () => setSelectedQuestion(record.query),
                                    style: { cursor: 'pointer' },
                                })}
                            />
                        )}
                    </Card>
                </>
            )}
        </div>
    );

    // ────────────────────────────────────────────────────────────
    // Section F: Prompt Impact tab
    // ────────────────────────────────────────────────────────────
    const impactColumns: ColumnsType<PromptImpactRow> = useMemo(() => [
        {
            title: 'Change',
            key: 'commit',
            width: 280,
            render: (_: unknown, row: PromptImpactRow) => (
                <div>
                    <Text strong style={{ fontSize: 12 }}>{row.message.slice(0, 80)}{row.message.length > 80 ? '...' : ''}</Text>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        <Tag style={{ fontSize: 10, marginRight: 4 }}>{row.sha.slice(0, 7)}</Tag>
                        {row.author} &middot; {formatDate(row.date)}
                    </div>
                </div>
            ),
        },
        {
            title: 'Pass Rate',
            key: 'passRate',
            width: 200,
            align: 'center' as const,
            render: (_: unknown, row: PromptImpactRow) => {
                if (row.avgPassRateBefore == null && row.avgPassRateAfter == null) return <Text type="secondary">-</Text>;
                const before = row.avgPassRateBefore != null ? Math.round(row.avgPassRateBefore * 100) : null;
                const after = row.avgPassRateAfter != null ? Math.round(row.avgPassRateAfter * 100) : null;
                const delta = before != null && after != null ? after - before : null;
                return (
                    <Space size={4}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{before != null ? `${before}%` : '-'}</Text>
                        <ArrowDownOutlined style={{ fontSize: 10, color: '#d9d9d9', transform: 'rotate(-90deg)' }} />
                        <Text strong style={{ fontSize: 12 }}>{after != null ? `${after}%` : '-'}</Text>
                        {delta != null && Math.abs(delta) >= 1 && (
                            <Tag
                                color={delta > 0 ? 'green' : 'red'}
                                icon={delta > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                style={{ fontSize: 10, margin: 0 }}
                            >
                                {delta > 0 ? '+' : ''}{delta}%
                            </Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Avg Similarity',
            key: 'similarity',
            width: 200,
            align: 'center' as const,
            render: (_: unknown, row: PromptImpactRow) => {
                if (row.avgSimilarityBefore == null && row.avgSimilarityAfter == null) return <Text type="secondary">-</Text>;
                const delta = row.avgSimilarityBefore != null && row.avgSimilarityAfter != null
                    ? row.avgSimilarityAfter - row.avgSimilarityBefore
                    : null;
                return (
                    <Space size={4}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{row.avgSimilarityBefore?.toFixed(2) ?? '-'}</Text>
                        <ArrowDownOutlined style={{ fontSize: 10, color: '#d9d9d9', transform: 'rotate(-90deg)' }} />
                        <Text strong style={{ fontSize: 12 }}>{row.avgSimilarityAfter?.toFixed(2) ?? '-'}</Text>
                        {delta != null && Math.abs(delta) >= 0.01 && (
                            <Tag
                                color={delta > 0 ? 'green' : 'red'}
                                icon={delta > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                style={{ fontSize: 10, margin: 0 }}
                            >
                                {delta > 0 ? '+' : ''}{delta.toFixed(2)}
                            </Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Runs',
            key: 'runs',
            width: 120,
            align: 'center' as const,
            render: (_: unknown, row: PromptImpactRow) => (
                <Text type="secondary" style={{ fontSize: 11 }}>
                    {row.runsBefore} before / {row.runsAfter} after
                </Text>
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 60,
            render: (_: unknown, row: PromptImpactRow) => (
                <Tooltip title="View change details">
                    <Button
                        type="text"
                        size="small"
                        icon={<DiffOutlined />}
                        onClick={() => setDiffModal({ sha: row.sha, message: row.message })}
                    />
                </Tooltip>
            ),
        },
    ], []);

    const promptImpactContent = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {!filters.tenant ? (
                <Empty
                    description="Select a Branch in the filter bar to see prompt change impact"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        This tab correlates prompt changes with eval metric changes
                    </Text>
                </Empty>
            ) : promptImpact.length === 0 ? (
                <Empty description="No prompt changes found for this branch" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
                <>
                    <Card
                        size="small"
                        title={
                            <Space>
                                <ExperimentOutlined />
                                <span>Prompt Change Impact</span>
                            </Space>
                        }
                        extra={<Text type="secondary" style={{ fontSize: 11 }}>{promptImpact.length} changes</Text>}
                    >
                        <Table
                            size="small"
                            rowKey="sha"
                            dataSource={promptImpact}
                            columns={impactColumns}
                            pagination={false}
                        />
                    </Card>
                </>
            )}

            <Modal
                title={diffModal ? `Change ${diffModal.sha.slice(0, 7)}` : ''}
                open={diffModal !== null}
                onCancel={() => setDiffModal(null)}
                footer={null}
                width={600}
            >
                {diffModal && (
                    <div>
                        <Text strong>{diffModal.message}</Text>
                        <div style={{ marginTop: 12 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Full SHA: {diffModal.sha}
                            </Text>
                        </div>
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                To view the diff, open the Prompts tab and use file history for this branch.
                            </Text>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );

    // ────────────────────────────────────────────────────────────
    // Section G: Insights banner
    // ────────────────────────────────────────────────────────────
    const insightsBanner = insights.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {insights.map((insight, i) => (
                <Alert
                    key={i}
                    type={insight.type}
                    message={insight.message}
                    showIcon
                    banner
                    style={{ borderRadius: 6 }}
                />
            ))}
        </div>
    ) : null;

    // ────────────────────────────────────────────────────────────
    // Main render
    // ────────────────────────────────────────────────────────────
    if (loading && runs.length === 0) {
        return <Spin style={{ display: 'block', margin: '60px auto' }} />;
    }

    const hasConsistencyRuns = consistencyRuns.length > 0;

    const tabItems = [
        { key: 'trends', label: 'Trends', children: trendsContent },
        {
            key: 'prompt-impact',
            label: (
                <span><ExperimentOutlined /> Prompt Impact</span>
            ),
            children: promptImpactContent,
        },
        ...(hasConsistencyRuns
            ? [{ key: 'consistency', label: 'Consistency', children: consistencyContent }]
            : []),
        { key: 'questions', label: 'Question Tracker', children: questionTrackerContent },
    ];

    return (
        <div
            style={{
                overflow: 'auto',
                height: skillStudioLayout != null ? sectionHeight : undefined,
                maxHeight: skillStudioLayout != null ? undefined : sectionHeight,
                paddingBottom: 24,
                minHeight: 0,
            }}
        >
            {filtersRow}
            {insightsBanner}
            {kpiCards}
            <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
        </div>
    );
};

export default SkillStudioDashboardSection;
